import {
  Typography,
  IconButton,
  Grid,
  Box,
  Button,
  Alert,
  Stack,
} from '@mui/material';
import dayjs, {Dayjs} from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {Add} from '@mui/icons-material';
import {TimePicker} from '@mui/x-date-pickers';
import {useCallback, useMemo, useState} from 'react';
import TimedMsgRequestWeekDayForm from './TimedMsgRequestWeekDayForm';
import TimedMsgRequestTimeRangePicker, {
  iTimedMsgTimeRange,
} from './TimedMsgRequestTimeRangePicker';
import {ITimedDuplicateTimes} from '../TimedMessageRequest';
import {ITimedMessageDayType} from '../../../../../dto/timed-message/TimedMessageRequest.dto';
import {daysOfWeek} from '../../../../../dto/DateTime.dto';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export interface iTimedMsgTimesItem {
  id: number;
  availableDays?: string[];
  selectedDays: string[];
  timeRanges: iTimedMsgTimeRange[];
}

type Props = {
  title: string;
  item: iTimedMsgTimesItem;
  selectedWeekdays: string[];
  enableWeekdays: boolean;
  removeBtnDisabled: boolean;
  onRemove: (id: number) => void;
  setDaysTimes: React.Dispatch<React.SetStateAction<iTimedMsgTimesItem[]>>;
  availableDays?: string[];
  duplicateTimes?: ITimedDuplicateTimes[];
  dateRange?: {
    start_date: dayjs.Dayjs | null;
    end_date: dayjs.Dayjs | null;
  };
  dayType: ITimedMessageDayType;
};

const MAX_ITEMS = 30;
const TimedMsgRequestTimesCard = ({
  title,
  item,
  selectedWeekdays,
  enableWeekdays,
  removeBtnDisabled,
  onRemove,
  setDaysTimes,
  availableDays,
  duplicateTimes,
  dateRange,
  dayType,
}: Props) => {
  const [time, setTime] = useState<Dayjs | null>(null);
  const handleAdd = useCallback(() => {
    if (time && time.isValid()) {
      setDaysTimes(prevItems => {
        const index = prevItems.findIndex(o => o.id === item.id);
        if (index === -1) return prevItems;
        const newItems = [...prevItems];
        const newItem = {
          ...newItems[index],
        } as iTimedMsgTimesItem;
        if (newItem.timeRanges[0]?.start_time !== null) {
          newItem.timeRanges = [
            ...newItem.timeRanges,
            {id: Date.now(), start_time: time},
          ];
        } else {
          newItem.timeRanges = [{id: Date.now(), start_time: time}];
        }

        newItems.splice(index, 1, newItem);
        return newItems;
      });
      setTime(null);
    }
  }, [item.id, setDaysTimes, time]);

  const handleRemove = () => {
    onRemove(item.id);
  };

  const isReachedMaxItems = useMemo(
    () => item.timeRanges.length === MAX_ITEMS,
    [item.timeRanges.length],
  );

  const handleTimeChange = useCallback((val: Dayjs | null) => {
    setTime(val);
  }, []);

  const timeExistenceText = useMemo(() => {
    if (!duplicateTimes?.length || !item?.timeRanges?.length) {
      return '';
    }

    const isMultiple = dayType === 'multiple';
    const days = new Set();
    const conflictDays = new Map<string, string>();

    for (const {date, times} of duplicateTimes) {
      const dayName = date.format('dddd');
      const isSlotHaveDaySelected = item.selectedDays.includes(dayName);

      // Check if date matches (for 'multiple') or day name matches (for others)
      const dateMatches =
        isMultiple &&
        dateRange &&
        date.isSameOrAfter(dateRange.start_date, 'day') &&
        date.isSameOrBefore(dateRange.end_date, 'day') &&
        isSlotHaveDaySelected;

      const dayMatches = !isMultiple && isSlotHaveDaySelected;

      if ((dateMatches || dayMatches) && !days.has(dayName)) {
        days.add(dayName);
        const matchedTimes = times
          .map(t => dayjs(t, 'HH:mm:ss'))
          .filter(t =>
            item.timeRanges.some(
              range => range.start_time?.isSame(t, 'second'),
            ),
          );

        if (matchedTimes.length) {
          const formattedTimes = matchedTimes
            .map(t => t.format('hh:mm A'))
            .join(' / ');
          conflictDays.set(dayName, formattedTimes);
        }
      }
    }

    // Convert to text in the correct order
    let result: string[] = [];
    daysOfWeek.forEach(day => {
      if (conflictDays.has(day)) {
        result.push(`${day}: ${conflictDays.get(day)}`);
      }
    });

    return result.join('\n');
  }, [dateRange, dayType, duplicateTimes, item.selectedDays, item.timeRanges]);

  return (
    <Grid
      container
      className='radius-5 min-w-400 max-w-400 h-full max-h-500 overflow-y scrollbar'>
      <Grid
        item
        xs={12}
        sx={{
          background: `var(--blue_variant41)`,
          borderRadius: '7px',
        }}
        className='p-4'>
        <Box className='flex justify-between items-center '>
          <Typography className='text-white-50'>{title}</Typography>
          {enableWeekdays && (
            <Button
              variant='text'
              aria-label='delete'
              onClick={handleRemove}
              disabled={removeBtnDisabled}
              sx={{color: `var(--red-variant1) !important`}}>
              Delete Slot
            </Button>
          )}
        </Box>

        {timeExistenceText ? (
          <Alert severity='error'>
            Time conflict:{' '}
            <Typography variant='caption' sx={{whiteSpace: 'pre-wrap'}}>
              {`\n${timeExistenceText}`}
            </Typography>
          </Alert>
        ) : null}
        <Stack direction='row'>
          {enableWeekdays && (
            <TimedMsgRequestWeekDayForm
              itemId={item.id}
              selected={item.selectedDays}
              selectedWeekdays={selectedWeekdays}
              setDaysTimes={setDaysTimes}
              availableDays={availableDays ?? []}
            />
          )}

          <Box className='ml-4'>
            <Stack direction='row' alignItems='center'>
              <Box>
                <Typography variant='body2'>Select Time</Typography>
                <TimePicker
                  className='mb-0 mr-6 w-150'
                  value={time}
                  onChange={handleTimeChange}
                  timeSteps={{hours: 1, minutes: 1, seconds: 60}}
                  slotProps={{
                    textField: {size: 'small'},
                    popper: {'aria-label': 'Select time dialog'},
                  }}
                />
              </Box>

              <IconButton
                className='w-40 h-40 font-bold radius-5'
                sx={{
                  background: `var(--blue-variant10)`,
                  border: `1px solid var(--blue_variant40)`,
                }}
                aria-label='add-time'
                onClick={handleAdd}
                disabled={isReachedMaxItems}>
                <Add />
              </IconButton>
            </Stack>

            <Box className='h-150 overflow-auto scrollbar mt-3'>
              {item.timeRanges.map(timeRange => (
                <TimedMsgRequestTimeRangePicker
                  key={timeRange.id}
                  itemId={item.id}
                  timeRange={timeRange}
                  setDaysTimes={setDaysTimes}
                  disableDelete={item.timeRanges.length === 1}
                />
              ))}
            </Box>
            {isReachedMaxItems && (
              <Typography variant='caption'>
                Maximum of {MAX_ITEMS} start time can be added for each request
              </Typography>
            )}
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

export default TimedMsgRequestTimesCard;
